import Image from "next/image";
import Link from "next/link";
import { X, Phone, Mail, MapPin } from "lucide-react";
import type { MenuSettings } from "./MobileMenu";
import {
  SOCIAL_ORDER,
  SOCIAL_ICONS,
  SOCIAL_BG,
} from "@/components/icons/SocialIcons";
import { cn } from "@/lib/format";

/**
 * Matches production's real canvas menu exactly (confirmed via the live
 * DOM): a dark right-side slide-in panel with the logo-light mark, tagline,
 * a flat link list (About Us/Why Heaven Himalaya/Contact Us/Blog/Travel
 * Info/Our Team/Shop — real static site links, not the mega-menu's
 * region→package tree), and a footer block with contact info + the same 6
 * real social icons as the site footer. Not the previous nested-collapse
 * tree of navigation.header, which doesn't match production's real
 * mobile-menu content at all.
 */
const CANVAS_LINKS = [
  { label: "About Us", href: "/about-us" },
  { label: "Why Heaven Himalaya?", href: "/why-heaven-himalaya" },
  { label: "Contact Us", href: "/contact-us" },
  { label: "Blog", href: "/blog" },
  { label: "Travel Info", href: "/travel-info" },
  { label: "Our Team", href: "/our-team" },
  { label: "Heaven Himalaya Shop", href: "/shop" },
] as const;

type MobileNavProps = {
  settings: MenuSettings;
  open: boolean;
  onClose: () => void;
};

function telHref(phone: string) {
  return `tel:${phone.replace(/[^\d+]/g, "")}`;
}

export default function CanvasNav({ settings, open, onClose }: MobileNavProps) {
  const { phone, email, address } = settings.contact ?? {};
  // tiktok has no settings.social column — it's a free-form custom_fields
  // entry, same as Footer.tsx's socialEntries.
  const socialLinks: Partial<Record<(typeof SOCIAL_ORDER)[number], string>> = {
    ...settings.social,
    tiktok: settings.custom_fields?.tiktok,
  };
  const socialEntries = SOCIAL_ORDER.filter((key) => socialLinks[key]).map(
    (key) => [key, socialLinks[key]!] as const,
  );

  return (
    <>
      <div
        aria-hidden={!open}
        onClick={onClose}
        className={`fixed hidden lg:block inset-0 z-[99] cursor-pointer bg-[rgba(15,23,42,0.251)] backdrop-blur-[4px] transition-opacity duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <div
        id="mobile-navigation"
        className={`fixed hidden lg:block  top-0 right-0 z-[1032] h-full w-full overflow-hidden bg-headings text-[#b6bcb9] transition-transform duration-500 ease-[cubic-bezier(0.785,0.135,0.15,0.86)] sm:w-[420px] md:w-[500px] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="custom-scroll-bar relative flex h-full max-h-full w-full flex-col overflow-auto p-8 md:p-[100px_54px]">
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className="icon absolute top-6 right-6 h-7 w-7 cursor-pointer text-white/70 hover:text-primary md:top-10"
          >
            <X className="h-full w-full" aria-hidden />
          </button>

          <div className="sidebar-header">
            <Link href="/" className="block max-w-[190px]" onClick={onClose}>
              <Image
                src="/logo-light.png"
                alt={settings.company_name}
                width={176}
                height={60}
                className="h-auto w-full"
              />
            </Link>
            <p className="brand-text mt-3 max-w-[300px] pl-7 text-sm font-medium">
              {settings.description}
            </p>
          </div>

          <div className="sidebar-body my-8 py-8">
            <ul className="grid -my-0.5 gap-y-1 pl-7">
              {CANVAS_LINKS.map((link) => (
                <li key={link.href} className="py-0.5">
                  <Link
                    href={link.href}
                    onClick={onClose}
                    className="text-md font-medium tracking-wide text-white/70 capitalize hover:text-white hover:underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="sidebar-footer mt-auto">
            <ul className="text-md leading-none tracking-wide [&>li+li]:mt-5">
              {phone ? (
                <li className="relative pl-9">
                  <Phone
                    className="icon absolute left-0 h-5 w-5 text-primary"
                    strokeWidth={2}
                    aria-hidden
                  />
                  <a href={telHref(phone)}>{phone}</a>
                </li>
              ) : null}
              {email ? (
                <li className="relative pl-9">
                  <Mail
                    className="icon absolute left-0 h-5 w-5 text-primary"
                    strokeWidth={2}
                    aria-hidden
                  />
                  <a href={`mailto:${email}`}>{email}</a>
                </li>
              ) : null}
              {address ? (
                <li className="relative pl-9">
                  <MapPin
                    className="icon absolute left-0 h-5 w-5 text-primary"
                    strokeWidth={2}
                    aria-hidden
                  />
                  {address}
                </li>
              ) : null}
            </ul>

            {socialEntries.length ? (
              <div className="mt-6 flex flex-wrap gap-1.5">
                {socialEntries.map(([key, url]) => {
                  const Icon = SOCIAL_ICONS[key];
                  return (
                    <a
                      key={key}
                      href={url}
                      rel="nofollow noreferrer"
                      target="_blank"
                      aria-label={key}
                      className={cn(
                        "inline-flex h-7 w-7 items-center justify-center rounded-sm text-white hover:opacity-80",
                        SOCIAL_BG[key],
                      )}
                    >
                      <Icon size={16} />
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
